import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Table } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type {
    TrialBalanceRowView,
    TrialBalanceTotalsView,
} from '../../trialBalanceMeta';

type TrialBalanceTableProps = {
    rows: TrialBalanceRowView[];
    totals: TrialBalanceTotalsView;
    /** Second drill hop — open the journal filtered to one real account. */
    onViewLedger: (accountId: string) => void;
    /** Footer note, e.g. "All figures in Georgian Lari (₾), rounded to the tetri." */
    footerNote: string;
    /** Footer stamp, e.g. "Generated 02/08/2026 14:20". */
    generatedText: string;
    /** Why the list is shorter than the chart — the server drops nil rows. */
    zeroNote: string;
};

const DRILL_CLASS_NAME =
    'ml-1 inline-flex h-auto min-h-0 cursor-[var(--cursor-interactive,pointer)] items-center whitespace-nowrap p-0 text-xs font-medium text-accent opacity-0 transition-opacity focus:opacity-100 group-hover:opacity-100';

/**
 * The trial balance itself — code, account, debit, credit, and the totals row.
 *
 * The OSS `Table` compound rather than Pro's `DataGrid`, on this folder's stated
 * rule: the totals row needs its own styling and `DataGrid` exposes neither a
 * footer slot nor a per-row `className`. It also renders fully expanded and
 * unvirtualized so A4 print works — the print block in `src/index.css` releases
 * the scroll container's clip and repeats `<thead>` across pages, exactly as it
 * does for the statements.
 *
 * Every row's side was decided by the **server**, not here: each carries an
 * explicit debit and credit, one of which is nil and renders as an en dash.
 * That is why accumulated depreciation — an asset with a credit balance — needs
 * no special case to appear in the credit column.
 *
 * The totals row is a `Table.Row` rather than a real `<tfoot>` because
 * `Table.Footer` is the container's footer slot (pagination and the like), not a
 * table-section footer. It is the last row, styled as the payoff, and it repeats
 * on paper what the totals bar above the table states on screen.
 */
export const TrialBalanceTable: React.FC<TrialBalanceTableProps> = ({
    rows,
    totals,
    onViewLedger,
    footerNote,
    generatedText,
    zeroNote,
}) => {
    const { t } = useTranslation('pages');

    return (
        <div>
            <div {...getNodeTestIdProp('trial-balance-table')}>
                <Table>
                    <Table.ScrollContainer>
                        <Table.Content
                            aria-label={t('trialBalance.ariaLabel')}
                            className="text-sm"
                        >
                            <Table.Header>
                                <Table.Column id="code">
                                    {t('trialBalance.columns.code')}
                                </Table.Column>
                                <Table.Column id="account" isRowHeader>
                                    {t('trialBalance.columns.account')}
                                </Table.Column>
                                <Table.Column
                                    id="debit"
                                    {...getNodeTestIdProp(
                                        'trial-balance-column-debit',
                                    )}
                                >
                                    {t('trialBalance.columns.debit')}
                                </Table.Column>
                                <Table.Column
                                    id="credit"
                                    {...getNodeTestIdProp(
                                        'trial-balance-column-credit',
                                    )}
                                >
                                    {t('trialBalance.columns.credit')}
                                </Table.Column>
                            </Table.Header>
                            <Table.Body>
                                {rows.map((row) => (
                                    <Table.Row
                                        className="group hover:bg-surface-secondary/60"
                                        id={row.accountId}
                                        key={row.accountId}
                                        {...getNodeTestIdProp(
                                            `trial-balance-row-${row.accountId}`,
                                        )}
                                    >
                                        <Table.Cell className="tabular-nums text-muted">
                                            {row.code}
                                        </Table.Cell>
                                        <Table.Cell>
                                            <span className="flex items-center gap-2">
                                                <span className="text-foreground">
                                                    {row.name}
                                                </span>
                                                <button
                                                    aria-label={t(
                                                        'trialBalance.viewLedgerFor',
                                                        { account: row.name },
                                                    )}
                                                    className={DRILL_CLASS_NAME}
                                                    onClick={() =>
                                                        onViewLedger(
                                                            row.accountId,
                                                        )
                                                    }
                                                    type="button"
                                                    {...getNodeTestIdProp(
                                                        `trial-balance-view-ledger-${row.accountId}`,
                                                    )}
                                                >
                                                    {t(
                                                        'trialBalance.viewLedger',
                                                    )}
                                                </button>
                                            </span>
                                        </Table.Cell>
                                        <Table.Cell
                                            className={row.debitClassName}
                                            {...getNodeTestIdProp(
                                                `trial-balance-debit-${row.accountId}`,
                                            )}
                                        >
                                            {row.debitText}
                                        </Table.Cell>
                                        <Table.Cell
                                            className={row.creditClassName}
                                            {...getNodeTestIdProp(
                                                `trial-balance-credit-${row.accountId}`,
                                            )}
                                        >
                                            {row.creditText}
                                        </Table.Cell>
                                    </Table.Row>
                                ))}

                                <Table.Row
                                    className="border-t-2 border-foreground/60 bg-surface-secondary"
                                    id="trial-balance-totals-row"
                                    {...getNodeTestIdProp(
                                        'trial-balance-totals-row',
                                    )}
                                >
                                    <Table.Cell />
                                    <Table.Cell className="font-bold text-foreground">
                                        {t('trialBalance.totals.rowLabel')}
                                    </Table.Cell>
                                    <Table.Cell
                                        className="text-right font-bold tabular-nums text-foreground"
                                        {...getNodeTestIdProp(
                                            'trial-balance-totals-row-debit',
                                        )}
                                    >
                                        {totals.debitText}
                                    </Table.Cell>
                                    <Table.Cell
                                        className="text-right font-bold tabular-nums text-foreground"
                                        {...getNodeTestIdProp(
                                            'trial-balance-totals-row-credit',
                                        )}
                                    >
                                        {totals.creditText}
                                    </Table.Cell>
                                </Table.Row>
                            </Table.Body>
                        </Table.Content>
                    </Table.ScrollContainer>
                </Table>
            </div>

            <div className="mt-3 flex flex-wrap justify-between gap-2 px-1 text-xs text-muted">
                <span {...getNodeTestIdProp('trial-balance-zero-note')}>
                    {zeroNote}
                </span>
                <span>{footerNote}</span>
                <span {...getNodeTestIdProp('trial-balance-generated')}>
                    {generatedText}
                </span>
            </div>
        </div>
    );
};
